
import Navbar from "@/components/Navbar";
import TokenDisplay from "@/components/TokenDisplay";
import GibRating from "@/components/GibRating";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import ImageHoverPopup from "@/components/ui/image-hover-popup";
import { Badge } from "@/components/ui/badge";
import { Fuel, CircuitBoard, Building2, BookOpen, Blocks, GraduationCap, Presentation } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

const YannisProfile = () => {
  const user = {
    name: "Dr. Yannis Kalfoglou, PhD",
    role: "iSPAI Chief Technical Advisor",
    solutionBounties: 12,
    domains: [
      "Artificial Intelligence",
      "Blockchain",
      "Semantic Web",
      "Ontology Mapping",
      "Innovation Management",
      "Enterprise Architecture",
      "Technology Strategy",
      "Capital Markets",
      "Oil & Gas"
    ],
    industries: [
      { name: "Technology", icon: CircuitBoard },
      { name: "Oil and Gas", icon: Fuel },
      { name: "Finance", icon: BookOpen },
      { name: "Blockchain", icon: Blocks },
      { name: "Education", icon: GraduationCap },
      { name: "Consulting", icon: Presentation }
    ],
    confirmedSolutions: 42,
    confirmedKnowledgeBases: 86,
    tokenBalance: 250,
    bio: "Dr. Yannis Kalfoglou brings over three decades of expertise in artificial intelligence, blockchain, and technology innovation to iSPAI. He has held AI leadership and AI strategy advisory roles at large multinationals companies like Ricoh, Invesco, Samsung, BP and Dell, where he drove AI strategy and led blockchain implementations across industries such as capital markets, oil & gas, and consumer electronics. Yannis holds a PhD and post-doc in AI and was an early pioneer in semantic web and ontology mapping. As a trusted advisor, he provides strategic guidance on AI development, blockchain integration, and innovation management. He has worked closely with venture capital firms, government bodies, and enterprise clients, helping them align emerging technologies with business outcomes. In 2021 Yannis wrote Blockchain for Business intended for the business audience technologist. In addition, Yannis has delivered over 80 presentations worldwide on advanced technology, positioning him as a thought leader in the fields of AI and blockchain. Through his advisory role at iSPAI, Yannis will provide guidance and work closely with our team on all aspects of the iSPAI platform."
  };

  return (
    <div className="min-h-screen bg-muted">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="p-6 col-span-2">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-start space-x-6">
                <div className="flex flex-col items-center gap-4">
                  <ImageHoverPopup 
                    imageSrc="/lovable-uploads/f40ee7b8-159f-4ea6-a372-f6f753f06539.png"
                    imageAlt="Dr. Yannis Kalfoglou"
                  >
                    <Avatar className="h-48 w-48 cursor-pointer">
                      <AvatarImage 
                        src="/lovable-uploads/f40ee7b8-159f-4ea6-a372-f6f753f06539.png" 
                        alt="Dr. Yannis Kalfoglou"
                      />
                      <AvatarFallback>YK</AvatarFallback>
                    </Avatar>
                  </ImageHoverPopup>
                  <div className="flex gap-2">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-16">BIO</Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-96">
                        <p className="text-sm leading-6">{user.bio}</p>
                      </PopoverContent>
                    </Popover>
                    <Button variant="outline" className="w-16">NW</Button>
                  </div>
                  <div className="w-full">
                    <h3 className="font-serif text-xl font-semibold text-black mb-3">Industries</h3>
                    <div className="grid grid-cols-1 gap-2">
                      {user.industries.map((industry) => {
                        const Icon = industry.icon;
                        return (
                          <div 
                            key={industry.name}
                            className="flex items-center space-x-2 p-3 border-2 border-black rounded-lg"
                          >
                            <Icon className="h-5 w-5 text-black" />
                            <span className="text-sm font-medium text-black">{industry.name}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <h2 className="font-serif text-3xl font-bold text-black">{user.name}</h2>
                    <a href="/ispai-channel" className="ml-2">
                      <img 
                        src="/lovable-uploads/d7afdb11-1c93-4596-9b11-91cb70981610.png" 
                        alt="iSPAI Channel" 
                        className="w-16 h-16 hover:scale-110 transition-transform"
                      />
                    </a>
                  </div>
                  <p className="text-lg font-medium text-black mb-4">{user.role}</p>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <Card className="p-6">
                      <div className="flex flex-col items-center">
                        <h3 className="font-serif text-xl font-semibold text-black mb-2">Solution Bounties</h3>
                        <span className="text-4xl font-bold mb-2">{user.solutionBounties}</span>
                        <Link 
                          to="/solution-bounties/yannis"
                          className="text-sm text-blue-600 hover:text-blue-800"
                        >
                          View Details →
                        </Link>
                      </div>
                    </Card>
                    <GibRating rating={9.8} />
                    <Card className="p-6">
                      <div className="flex flex-col items-center">
                        <h3 className="font-serif text-xl font-semibold text-black mb-2">Confirmed Solutions</h3>
                        <span className="text-4xl font-bold">{user.confirmedSolutions}</span>
                      </div>
                    </Card>
                    <Card className="p-6">
                      <div className="flex flex-col items-center">
                        <h3 className="font-serif text-xl font-semibold text-black mb-2">Knowledge Bases</h3>
                        <span className="text-4xl font-bold">{user.confirmedKnowledgeBases}</span>
                      </div>
                    </Card>
                  </div>
                </div>
              </div>
            </div>
          </Card>
          
          <div className="space-y-6">
            <TokenDisplay balance={user.tokenBalance} />
            <Card className="p-6">
              <h3 className="font-serif text-xl font-semibold text-black mb-3">Domains</h3>
              <div className="flex flex-wrap gap-2">
                {user.domains.map((domain) => (
                  <Badge 
                    key={domain} 
                    variant="outline"
                    className="border-2 border-black text-black"
                  >
                    {domain}
                  </Badge>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default YannisProfile;
